import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDragEnter, CdkDragExit } from '@angular/cdk/drag-drop';
import { formatDate } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SingleOrDefault } from 'src/app/_helpers/ArrayExtensionMethodHelper.functions';
import { GroupedEditorActor } from 'src/app/models/requestEditor';
import { RequestBuilderService } from '../../request-builder.service';
import { SendGroupModel } from 'src/app/models/sendgroup.model';
import { DocumentModel } from 'src/app/models/document.model';
import { Subscription } from 'rxjs';
import { DocumentActor } from 'src/app/models/actors';

@Component({
  selector: 'send-group',
  templateUrl: './send-group.component.html',
  styleUrl: './send-group.component.scss'
})
export class SendGroupComponent implements OnDestroy {
    private documentsSubscription!: Subscription;
    private actorsSubscription!: Subscription;
    private personsSubscription!: Subscription;
    private requestIdSubscription!: Subscription;
  
    private _sendgroup!: SendGroupModel;
    private _requestGuid!: string;
    
    get sendgroup(): SendGroupModel {
      return this._sendgroup;
    }
    @Input() set sendgroup(val: SendGroupModel){
      this._sendgroup = val;
      if(this._sendgroup){
        this.getSendgroupWithGroupedActors();
      }
    }
    @Input() sendgroups: SendGroupModel[] = [];
    @Input() sendgroupDeadlines: SendGroupModel[] = [];
    @Input() sortingAllowed: boolean = false;
    
    
    isFirstSendGroup: boolean = false;
    orderIndex!: number;
    isLoading: boolean = false;
    public selectedMoment: any;
    translations = <any>{};
    latestDeadline: Date = new Date();
    groupedActors: GroupedEditorActor[] = [];
    documents: DocumentModel[] = [];
    actors: DocumentActor[] = [];
    persons: DocumentActor[] = [];
        
    constructor(private translateService: TranslateService, protected requestBuilderService: RequestBuilderService) {
        this.getTranslations();
        translateService.onLangChange.subscribe(() => {
          this.getTranslations();
        });
        this.requestIdSubscription = this.requestBuilderService.requestGuid$.subscribe(requestGuid => {
          this._requestGuid = requestGuid;
        });
        this.documentsSubscription = this.requestBuilderService.documents$.subscribe(documents => {
          this.documents = documents;
        });
        this.actorsSubscription = this.requestBuilderService.actors$.subscribe(actors => {
          this.actors = actors;
        });
        this.personsSubscription = this.requestBuilderService.persons$.subscribe(persons => {
          this.persons = persons;
        });
    }
    
    ngOnDestroy(): void {
      if (this.documentsSubscription) {
        this.documentsSubscription.unsubscribe();
      }
      if (this.actorsSubscription) {
          this.actorsSubscription.unsubscribe();
      }
      if (this.personsSubscription) {
          this.personsSubscription.unsubscribe();
      }
      if (this.requestIdSubscription) {
          this.requestIdSubscription.unsubscribe();
      }
    }

    getTranslations() {
        this.translateService.get([
        'Request.Status.Latest',
        'Request.Status.DraggingInsideSendgroup',
        'Toasts.Error',
        ]).subscribe(translation => {
          this.translations.requestStatusLatest = translation['Request.Status.Latest'];
          this.translations.requestDraggingInsideSendgroup = translation['Request.Status.DraggingInsideSendgroup'];
          this.translations.toastError = translation['Toasts.Error'];
        });
    }

    getCorrectSendDate() {
        return this.translations.requestStatusLatest + " " + new Date(new Date().setDate(new Date().getDate() + this.sendgroup.deadlineDays!)).toLocaleString();
    }

    calculateLatestDeadline(days: number) {
        if (days < 31) return new Date(new Date(this.latestDeadline!).setDate(new Date(this.latestDeadline!).getDate() + days));
        if (days === 31) return new Date(new Date(this.latestDeadline!).setMonth(new Date(this.latestDeadline!).getMonth() % 12 + 1));
        if (days === 62) return new Date(new Date(this.latestDeadline!).setMonth(new Date(this.latestDeadline!).getMonth() % 12 + 2));
        if (days === 93) return new Date(new Date(this.latestDeadline!).setMonth(new Date(this.latestDeadline!).getMonth() % 12 + 3));
        return new Date();
    }

    public changeSendDate(event: any){
        this.sendgroup.deadline = event;
    }

    public getPreviousDeadlineAsString(date: Date):string{
      return formatDate(date, "dd-MM-yyyy HH:mm", "en-US");
    }

    public getSendgroupWithGroupedActors() {
      if(this.sendgroups.filter(sendgroup => sendgroup.name !== "_SendGroup").length > 0){
        this.orderIndex = this.sendgroups.filter(sendgroup => sendgroup.name !== "_SendGroup").findIndex(sg => sg.sendGroupGuid === this.sendgroup.sendGroupGuid)
        this.isFirstSendGroup = this.orderIndex === 0;
      }
      this.sendgroup.actions.forEach(action => {
        if(this.sendgroup.actions.filter(s => s.personGuid === action.personGuid).length > 1){
          if(!this.groupedActors.map(actor => actor.id).includes(action.personGuid)) {
            let actionsById = this.actors.filter(s => s.dossierPersonId === action.personGuid);
            let amountOfDocuments = actionsById.length;
            let amountOfCredits = actionsById.reduce((sum, current) => sum + current.suggestedCreditCost, 0);
            let amountOfActions = actionsById.length;
            this.groupedActors.push({
              id: action.personGuid,
              name: SingleOrDefault(this.actors, 'dossierPersonId', action.personGuid)?.fullName,
              order: action.order,
              countOfAction: amountOfActions,
              countOfCredits: amountOfCredits,
              countOfDocuments: amountOfDocuments,
              grouped: true
            });
            this.sendgroup.actions.filter(s => s.id === action.personGuid).splice(0);
          }
        } else {
          let actor = SingleOrDefault(this.actors, 'dossierPersonId', action.personGuid);
          this.groupedActors.push({
            id: action.personGuid,
            name: actor?.fullName,
            actionType: action.actionType,
            order: action.order,
            credits: actor?.suggestedCreditCost,
            documentName: SingleOrDefault(this.documents, 'id', actor?.documentId)?.name,
            grouped: false
          });
          this.sendgroup.actions.filter(s => s.id === action.personGuid).splice(0);
        }
  
      });
    }
     
    public getGroupedActor(id: string) {
      let actions: GroupedEditorActor[] = [];
      let sendgroupActions = this.actors.filter(action => action.dossierPersonId === id);
      sendgroupActions.forEach(action => {
        actions.push({
          id: action.id,
          actionType: action.action,
          order: action.order,
          credits: action.credits,
          documentName: SingleOrDefault(this.documents, 'id', action.documentId)?.name,
          grouped: false
        });
      });
      return actions;
    }

    dropItem(event: any) {
      this.isLoading = false;
      if (event.previousContainer.id !== event.container.id) {
        transferArrayItem(event.previousContainer.data,
          event.container.data,
          event.previousIndex,
          event.currentIndex);
        let sendgroupOrder = this.sendgroups.find(s => s.sendGroupGuid === event.container.id)?.order;
        let listOfDraggedActors = event.container.data.map((cd:any) => cd.id);
        if(!sendgroupOrder) sendgroupOrder = 0;
        if(event.container.id.toLocaleLowerCase().includes("placeholder")){
          this.requestBuilderService.createSendgroup(this._requestGuid, sendgroupOrder, listOfDraggedActors);
        } else {
          this.requestBuilderService.assignToSendgroup(this._requestGuid, event.container.id, listOfDraggedActors);
        }
      }
    }
    
    getConnectedList(): any[] {
      return this.sendgroups.filter(d => d.sendGroupGuid !== this.sendgroup.sendGroupGuid).map(x => x.sendGroupGuid);
    }

    dropGroup(event: CdkDragDrop<any[]>) {
      moveItemInArray(this.sendgroups, event.previousIndex, event.currentIndex);
    }

    dragStarted(event: any) {
      // this.isLoading = true;
    }

    dragEnded(event: any) {
      // this.isLoading = true;
    }

    enter(entered: CdkDragEnter<any>, guid: string) {
      if (guid === this._sendgroup.sendGroupGuid) {
        document.getElementById(guid)?.classList.add('hideDatalist');
      }
      this.isLoading = true;
    }

    exit(exit: CdkDragExit<any>, guid: string) {
      if (guid === this._sendgroup.sendGroupGuid) {
        document.getElementById(guid)?.classList.remove('hideDatalist');
      }
      this.isLoading = false;
    }

    getTranslatedSendGroupName(sendGroupName: String){
      if(!sendGroupName){
        return;
      }
      if(sendGroupName.toLocaleLowerCase().startsWith("sendgroup")){
        let sendGroupStringArray = sendGroupName.split(" ");
        if(sendGroupStringArray.length > 1){
          return this.translateService.instant('Request.Send.' + sendGroupStringArray[0], { number: sendGroupStringArray[1] });
        } else {
          return this.translateService.instant('Request.Send._' + sendGroupStringArray[0]);
        }
      } else if(sendGroupName.toLocaleLowerCase() === "_sendgroup"){
        return this.translateService.instant('Request.Send.' + sendGroupName);
      }
      return sendGroupName;
    }

    updateSendGroup(item: string, event: any){
      if(item === 'name'){
        this._sendgroup.name = (event.target as HTMLInputElement).value;
        this.requestBuilderService.updateSendgroupName(this.sendgroup.sendGroupGuid, this._sendgroup.name);
      } else if(item === 'deadlineDate'){
        this._sendgroup.deadline = event;
        this.requestBuilderService.updateSendgroupDeadline(this.sendgroup.sendGroupGuid, this._sendgroup.deadline);
      } else if(item === 'deadlineDays'){
        this._sendgroup.deadlineDays = event;
        this.requestBuilderService.updateSendgroupDeadlineDays(this.sendgroup.sendGroupGuid, this._sendgroup.deadlineDays);
      }
    }

    public collapseCurrentSendgroup(sendGroupId: string){
      let collapsedSendgroupIndex = this.requestBuilderService.collapsedSendgroups.findIndex(s => s === sendGroupId);
      if(collapsedSendgroupIndex > -1){
        this.requestBuilderService.collapsedSendgroups.splice(collapsedSendgroupIndex, 1);
      } else {
        this.requestBuilderService.collapsedSendgroups.push(sendGroupId);
      }
    }

    public isCurrentSendGroupCollapsed(sendGroupId: string){
      return !this.requestBuilderService.collapsedSendgroups.includes(sendGroupId);
    }
}
